import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW } from "@/lib/helpers";
import {
  PageHeader, BtnPrimary, BtnSecondary, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Minus, CheckCircle, ArrowsClockwise, Warning, EnvelopeOpen, FilePdf, FileXls, Clock } from "@phosphor-icons/react";
import { PhotoThumb } from "@/components/PhotoUploader";
import { API_BASE } from "@/lib/api";
import MicDictate from "@/components/MicDictate";

/**
 * StockTake — mobile-first quick form.
 * - Big tappable +/- buttons
 * - Search inventory by SKU or name
 * - Stages counted values locally; submits in one batch
 */
export default function StockTake() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({}); // sku -> counted number (string)
  const [note, setNote] = useState("Monthly stock-take");
  const [busy, setBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);  // { id, note, count } after successful submit
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  async function load() {
    try { const { data } = await api.get("/inventory"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadHistory() {
    try { const { data } = await api.get("/inventory/stocktakes?limit=20"); setHistory(data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  useEffect(() => { load(); loadHistory(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter(
      (i) => i.sku.toLowerCase().includes(s) ||
             (i.name || "").toLowerCase().includes(s) ||
             (i.category || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  function setCount(sku, val) {
    setCounts((c) => ({ ...c, [sku]: val }));
  }
  function bump(sku, delta, current) {
    const next = Math.max(0, Number(current ?? 0) + delta);
    setCount(sku, String(next));
  }
  function fillFromSystem(sku, qty) { setCount(sku, String(qty)); }
  function clear(sku) { setCounts((c) => { const n = { ...c }; delete n[sku]; return n; }); }

  const staged = useMemo(() => {
    const lines = [];
    for (const [sku, val] of Object.entries(counts)) {
      const num = Number(val);
      if (Number.isFinite(num)) {
        const item = items.find((i) => i.sku === sku);
        if (item) lines.push({ sku, counted: num, prev: item.quantity, name: item.name });
      }
    }
    return lines;
  }, [counts, items]);

  async function submit() {
    if (staged.length === 0) {
      toast.error("Nothing to submit.");
      return;
    }
    setBusy(true);
    try {
      const { data: out } = await api.post("/inventory/stocktake", {
        lines: staged.map((s) => ({ sku: s.sku, counted: s.counted })),
        note,
      });
      toast.success(`Stock-take saved: ${staged.length} SKUs updated`);
      const batchId = out?.batch_id || null;
      setLastBatch(batchId ? { id: batchId, note, count: staged.length } : null);
      // Auto-email a summary (with PDF + XLSX attached) to all managers
      try {
        const results = staged.map((s) => ({
          sku: s.sku, previous: s.prev, counted: s.counted,
          delta: Number((s.counted - s.prev).toFixed(2)),
        }));
        const { data: r } = await api.post("/inventory/stocktake/email-report",
          { results, note, batch_id: batchId });
        if (r?.ok) {
          toast.success(`Report ${r.attached ? "(PDF+XLSX) " : ""}emailed to ${r.recipients} manager${r.recipients === 1 ? "" : "s"}`);
        } else if (r?.note) toast.message(r.note);
      } catch (mailErr) {
        toast.message("Stock saved, but summary email could not be sent");
      }
      setCounts({});
      load();
      loadHistory();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  function downloadUrl(batchId, kind) {
    return `${API_BASE}/inventory/stocktake/${batchId}/${kind}`;
  }

  return (
    <div data-testid="stocktake-page" className="pb-32">
      <PageHeader
        overline="Module / Inventory"
        title="Stock-Take"
        subtitle="Walk the warehouse, tap +/− to count. Submit at the end — system updates everything in one go."
        actions={
          <BtnSecondary onClick={() => { setShowHistory((v) => !v); loadHistory(); }} data-testid="stocktake-history-toggle">
            <Clock size={14} weight="bold" className="inline mr-1.5" />
            {showHistory ? "Hide history" : `History (${history.length})`}
          </BtnSecondary>
        }
      />

      {/* Just-submitted banner with PDF/XLSX */}
      {lastBatch && (
        <div className="phizo-card border-2 border-[#00A142] bg-[#E6F7EE] p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="stocktake-last-batch">
          <CheckCircle size={28} weight="fill" className="text-[#00A142]" />
          <div className="flex-1 min-w-0">
            <div className="phizo-label text-[#00A142]">Last submission</div>
            <div className="font-semibold text-[#09090b]">{lastBatch.note} · {lastBatch.count} SKU{lastBatch.count === 1 ? "" : "s"}</div>
            <div className="text-xs text-[#52525b] mono">ID {lastBatch.id.slice(0, 8)}</div>
          </div>
          <a
            href={downloadUrl(lastBatch.id, "pdf")}
            target="_blank" rel="noreferrer"
            data-testid="stocktake-download-pdf"
            className="inline-flex items-center gap-1.5 border border-[#09090b] bg-[#09090b] text-white px-3 py-2 text-xs font-bold hover:bg-[#27272a]"
          >
            <FilePdf size={14} weight="bold" /> Download PDF
          </a>
          <a
            href={downloadUrl(lastBatch.id, "xlsx")}
            target="_blank" rel="noreferrer"
            data-testid="stocktake-download-xlsx"
            className="inline-flex items-center gap-1.5 border border-[#00A142] bg-[#00A142] text-white px-3 py-2 text-xs font-bold hover:bg-[#008f3a]"
          >
            <FileXls size={14} weight="bold" /> Download Excel
          </a>
          <button
            onClick={() => setLastBatch(null)}
            className="text-xs text-[#52525b] hover:text-[#09090b] px-2 py-2"
            data-testid="stocktake-dismiss-banner"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* History drawer */}
      {showHistory && (
        <div className="phizo-card mb-4" data-testid="stocktake-history">
          <div className="p-4 border-b border-[#e4e4e7] flex items-center justify-between">
            <div>
              <div className="phizo-label">Past Stock-takes</div>
              <div className="font-heading text-base font-bold">Most recent 20</div>
            </div>
          </div>
          {history.length === 0 ? (
            <div className="p-6"><Empty label="No prior stock-takes recorded yet." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="phizo-table">
                <thead>
                  <tr>
                    <th>When</th><th>By</th><th>Note</th>
                    <th className="text-right">Items</th>
                    <th>Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b) => {
                    const vd = Number(b.total_value_delta || 0);
                    const flagged = Math.abs(vd) > 5000;
                    return (
                    <tr key={b.id} data-testid={`stocktake-hist-${b.id}`}>
                      <td className="mono text-xs">{new Date(b.at).toLocaleString("en-GB")}</td>
                      <td>{b.by_user_name}</td>
                      <td className="text-[#52525b]">
                        <div className="flex items-center gap-2">
                          {b.note}
                          {flagged && (
                            <span
                              data-testid={`stocktake-hist-variance-${b.id}`}
                              title={`Value variance K ${vd.toLocaleString()} exceeds K 5,000 — review`}
                              className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-[#FFF0F0] text-[#FF2A2A] border border-[#FF2A2A] font-bold"
                            >
                              <Warning size={10} weight="fill" /> Variance K {Math.abs(vd).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-right mono font-semibold">{b.count}</td>
                      <td>
                        <div className="flex gap-2">
                          <a
                            href={downloadUrl(b.id, "pdf")}
                            target="_blank" rel="noreferrer"
                            data-testid={`stocktake-hist-pdf-${b.id}`}
                            className="text-xs font-bold text-[#09090b] hover:underline inline-flex items-center gap-1">
                            <FilePdf size={12} weight="bold" /> PDF
                          </a>
                          <a
                            href={downloadUrl(b.id, "xlsx")}
                            target="_blank" rel="noreferrer"
                            data-testid={`stocktake-hist-xlsx-${b.id}`}
                            className="text-xs font-bold text-[#00A142] hover:underline inline-flex items-center gap-1">
                            <FileXls size={12} weight="bold" /> Excel
                          </a>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="phizo-card p-3 mb-4 sticky top-14 z-10 bg-white">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-3 text-[#a1a1aa]" />
          <input
            data-testid="stocktake-search"
            placeholder="Search SKU, name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-[#e4e4e7] bg-white pl-10 pr-3 py-3 text-base focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
          />
        </div>
        <div className="mt-2 text-xs text-[#52525b] flex items-center justify-between">
          <span><strong>{staged.length}</strong> staged · {items.length} total SKUs</span>
          {staged.length > 0 && (
            <button onClick={() => setCounts({})} className="text-[#FF2A2A] font-semibold">Clear all</button>
          )}
        </div>
      </div>

      {filtered.length === 0 && <Empty label="No SKUs match your search." />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((i) => {
          const counted = counts[i.sku];
          const num = Number(counted);
          const hasCount = counted !== undefined && counted !== "";
          const delta = hasCount ? num - i.quantity : 0;
          return (
            <div key={i.id} data-testid={`stocktake-card-${i.sku}`} className="phizo-card p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-3 min-w-0">
                  <PhotoThumb value={i.photo_url} size={44} />
                  <div className="min-w-0">
                    <div className="phizo-label mono">{i.sku}</div>
                    <div className="font-semibold text-base text-[#09090b] mt-1 truncate">{i.name}</div>
                    <div className="text-xs text-[#52525b]">{i.category} · {i.location}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="phizo-label">System</div>
                  <div className="font-heading text-2xl font-black mt-1">{i.quantity}</div>
                  <div className="text-[10px] text-[#a1a1aa]">{i.unit}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <button
                  data-testid={`stocktake-minus-${i.sku}`}
                  onClick={() => bump(i.sku, -1, counted ?? i.quantity)}
                  className="h-14 w-14 bg-[#f4f4f5] hover:bg-[#e4e4e7] active:bg-[#d4d4d8] text-2xl font-bold flex items-center justify-center transition-colors"
                  aria-label="Decrease"
                >
                  <Minus size={20} weight="bold" />
                </button>
                <input
                  data-testid={`stocktake-input-${i.sku}`}
                  type="number" min="0" step="0.1"
                  inputMode="decimal"
                  className="flex-1 h-14 border border-[#e4e4e7] text-center font-heading text-2xl font-black focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="—"
                  value={counted ?? ""}
                  onChange={(e) => setCount(i.sku, e.target.value)}
                />
                <button
                  data-testid={`stocktake-plus-${i.sku}`}
                  onClick={() => bump(i.sku, 1, counted ?? i.quantity)}
                  className="h-14 w-14 bg-[#002FA7] hover:bg-[#002080] active:bg-[#001a66] text-white flex items-center justify-center transition-colors"
                  aria-label="Increase"
                >
                  <Plus size={20} weight="bold" />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <button
                    onClick={() => fillFromSystem(i.sku, i.quantity)}
                    className="text-xs text-[#52525b] hover:text-[#09090b] underline">
                    Match system
                  </button>
                  {hasCount && (
                    <button
                      onClick={() => clear(i.sku)}
                      className="text-xs text-[#FF2A2A] hover:underline ml-3">
                      Reset
                    </button>
                  )}
                </div>
                {hasCount ? (
                  <div className={`text-xs mono font-bold ${delta > 0 ? "text-[#00A142]" : delta < 0 ? "text-[#FF2A2A]" : "text-[#52525b]"}`}>
                    {delta >= 0 ? "+" : ""}{delta} {delta < 0 && i.quantity + delta < i.reorder_level && (
                      <Warning size={12} weight="fill" className="inline ml-1" />
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-[#a1a1aa]">Not counted</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky submit bar */}
      {staged.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-20 bg-[#09090b] text-white p-4 border-t-4 border-[#002FA7]">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              data-testid="stocktake-note"
              placeholder="Note (e.g. May 2026 month-end)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="flex-1 bg-transparent border border-white/30 px-3 py-2 text-sm text-white placeholder:text-white/50 focus:border-white outline-none"
            />
            <MicDictate
              context="stock take note; mining; aggregates"
              onTranscript={(text) => setNote((n) => (n ? n + " " : "") + text)}
              testId="stocktake-note-mic"
            />
            <div className="text-sm">
              Submitting <strong>{staged.length}</strong> count{staged.length === 1 ? "" : "s"}
            </div>
            <button
              data-testid="stocktake-submit"
              onClick={submit}
              disabled={busy}
              className="bg-[#00C853] hover:bg-[#00A142] active:bg-[#008f3a] text-[#09090b] font-bold px-6 py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              <CheckCircle size={18} weight="bold" /> {busy ? "Saving…" : "Submit Stock-Take"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
